import { useState, type ReactNode } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ApiResourceTable,
  type ApiResourceExtraColumn,
} from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { csrInquiryService } from "@project/services/admin";
import type {
  CsrInquiryPayload,
  CsrInquiryRecord,
} from "@project/types/csr-inquiry";
import {
  CSR_AREAS,
  CSR_CONTRIBUTIONS,
  CSR_DURATIONS,
} from "@project/user/components/csr/csr-options";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import {
  Briefcase,
  Building2,
  CalendarClock,
  Handshake,
  IndianRupee,
  Mail,
  MessageSquare,
  Phone,
  ShieldCheck,
  User,
} from "lucide-react";

const toOptions = (list: { value: string }[]) =>
  list.map(o => ({ value: o.value, label: o.value }));

const fields: Field[] = [
  {
    name: "company_name",
    label: "Company / Organisation",
    required: true,
    showInTable: true,
    placeholder: "e.g. ABC Foundation",
  },
  {
    name: "contact_person_name",
    label: "Contact Person",
    required: true,
    showInTable: true,
    placeholder: "e.g. Rahul Sharma",
  },
  {
    name: "official_email",
    label: "Official Email",
    type: "email",
    required: true,
    showInTable: true,
    placeholder: "e.g. csr@company.com",
  },
  {
    name: "mobile_number",
    label: "Mobile Number",
    type: "tel",
    required: true,
    showInTable: true,
    placeholder: "e.g. +91 9876543210",
  },
  {
    name: "designation",
    label: "Designation",
    showInTable: false,
    placeholder: "e.g. CSR Manager",
  },
  {
    name: "partnership_interests",
    label: "Partnership Interests",
    type: "tags",
    required: true,
    showInTable: false,
    placeholder: "Type an area and press Enter…",
    help: `Options: ${CSR_AREAS.map(a => a.value).join(", ")}`,
  },
  {
    name: "estimated_contribution",
    label: "Estimated Contribution",
    type: "select",
    options: toOptions(CSR_CONTRIBUTIONS),
    showInTable: false,
  },
  {
    name: "partnership_duration",
    label: "Partnership Duration",
    type: "select",
    options: toOptions(CSR_DURATIONS),
    showInTable: false,
  },
  {
    name: "message",
    label: "Message",
    type: "textarea",
    showInTable: false,
    placeholder: "CSR goals or queries...",
  },
  {
    name: "privacy_consent",
    label: "Privacy Consent",
    type: "boolean",
    showInTable: false,
  },
  // {
  //   name: "created_at",
  //   label: "Submitted On",
  //   type: "date",
  //   readOnly: true,
  //   showInTable: true,
  // },
];

function splitInterests(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(v => String(v).trim()).filter(Boolean);
  }
  return String(value ?? "")
    .split(",")
    .map(v => v.trim())
    .filter(Boolean);
}

const extraColumns: ApiResourceExtraColumn<CsrInquiryRecord>[] = [
  {
    id: "interests",
    label: "Interests",
    headerClassName: "w-64",
    cellClassName: "w-64",
    render: row => {
      const tags = splitInterests(row.partnership_interests);
      if (!tags.length) {
        return "—";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map(tag => (
            <Badge
              key={tag}
              variant="outline"
              className="border-primary/30 bg-primary/5 font-medium text-primary"
            >
              {tag}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: "contribution",
    label: "Contribution",
    headerClassName: "w-40",
    cellClassName: "w-40 whitespace-nowrap",
    render: row => row.estimated_contribution ?? "—",
  },
];

function toPayload(raw: Record<string, unknown>): CsrInquiryPayload {
  const optional = (key: string) => {
    const v = String(raw[key] ?? "").trim();
    return v || undefined;
  };
  return {
    company_name: String(raw.company_name ?? "").trim(),
    contact_person_name: String(raw.contact_person_name ?? "").trim(),
    official_email: String(raw.official_email ?? "").trim(),
    mobile_number: String(raw.mobile_number ?? "").trim(),
    designation: optional("designation"),
    partnership_interests: splitInterests(raw.partnership_interests).join(", "),
    estimated_contribution: optional("estimated_contribution"),
    partnership_duration: optional("partnership_duration"),
    message: optional("message"),
    privacy_consent:
      raw.privacy_consent === true || raw.privacy_consent === "true",
  };
}

function formatDateTime(value?: string) {
  if (!value) {
    return undefined;
  }
  // API sends "+0000" offsets; normalise to "+00:00" for reliable parsing.
  const date = new Date(value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function InfoRow({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: typeof User;
  label: string;
  value?: string | null;
  children?: ReactNode;
}) {
  if (!value && !children) {
    return null;
  }
  return (
    <div className="flex items-start gap-3">
      <div className="bg-primary/10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {children ? (
          <div className="mt-0.5">{children}</div>
        ) : (
          <p className="mt-0.5 break-words text-sm font-semibold text-foreground">
            {value}
          </p>
        )}
      </div>
    </div>
  );
}

export default function CsrInquiriesPage() {
  const [selected, setSelected] = useState<CsrInquiryRecord | null>(null);
  const interests = selected
    ? splitInterests(selected.partnership_interests)
    : [];

  return (
    <AdminShell
      title="CSR Inquiries"
      subtitle="Manage CSR partnership enquiries submitted from the website"
    >
      <ApiResourceTable<CsrInquiryRecord>
        title="CSR Inquiry"
        labelField="company_name"
        fields={fields}
        extraColumns={extraColumns}
        searchFields={[
          "company_name",
          "contact_person_name",
          "official_email",
          "mobile_number",
        ]}
        defaultValues={{ privacy_consent: true }}
        enableCsvExport
        csvFileName="csr-inquiries"
        list={query => csrInquiryService.listPage(query)}
        loadRecordForEdit={id => csrInquiryService.get(id)}
        mapRecordToForm={record => ({
          ...record,
          partnership_interests: splitInterests(
            record.partnership_interests
          ) as unknown as string,
        })}
        create={payload => csrInquiryService.create(toPayload(payload))}
        update={(id, payload) =>
          csrInquiryService.update(id, toPayload(payload))
        }
        remove={id => csrInquiryService.delete(id)}
        onView={row => setSelected(row)}
      />

      <Dialog
        open={selected !== null}
        onOpenChange={open => {
          if (!open) {
            setSelected(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <span className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <Handshake className="h-4 w-4 text-primary" />
              </span>
              CSR Inquiry Details
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="!bg-white rounded-xl border border-border px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Company / Organisation
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {selected.company_name}
                </p>
                {formatDateTime(selected.created_at) && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Submitted on {formatDateTime(selected.created_at)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoRow
                  icon={User}
                  label="Contact Person"
                  value={selected.contact_person_name}
                />
                <InfoRow
                  icon={Briefcase}
                  label="Designation"
                  value={selected.designation}
                />
                <InfoRow
                  icon={Mail}
                  label="Official Email"
                  value={selected.official_email}
                />
                <InfoRow
                  icon={Phone}
                  label="Mobile Number"
                  value={selected.mobile_number}
                />
                <InfoRow
                  icon={IndianRupee}
                  label="Estimated Contribution"
                  value={selected.estimated_contribution}
                />
                <InfoRow
                  icon={CalendarClock}
                  label="Partnership Duration"
                  value={selected.partnership_duration}
                />
                <InfoRow icon={ShieldCheck} label="Privacy Consent">
                  {selected.privacy_consent ? (
                    <Badge
                      variant="outline"
                      className="border-emerald-200 bg-emerald-50 text-emerald-700"
                    >
                      Accepted
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-red-200 bg-red-50 text-red-600"
                    >
                      Not accepted
                    </Badge>
                  )}
                </InfoRow>
              </div>

              {interests.length > 0 && (
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Partnership Interests
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {interests.map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="border-primary/30 bg-primary/5 font-medium text-primary"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selected.message && (
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Message
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {selected.message}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
